import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import clsx from 'clsx';
import GradientColorHeader from '@icoach/components/dialog/GradientColorHeader';
import { CheckoutPaidListBox, CreditOrderList } from '@icoach/accountBook/components';
import { InvoiceCard } from '@icoach/accountBook/AccountBookCreateOrderPage';
import { EmployeeSelect } from '@icoach/components/';
import { refIsRequiredError, isFunction, isEmpty } from '@util/utils';
import useOrderCheckoutApi from '@apis/useOrderCheckoutApi';
import { Alert, Button, Form } from "@curves/ui-base";
import { parseDate } from '@util/moment';

const NoInformationDialogContent = () => {
    return (
        <DialogContent className="bg-blue-gray-50 container main-container-spacing">
            <Alert variant="standard" severity="info" color="info">
                沒有欠費資料
            </Alert>
        </DialogContent>
    );
};
const CheckoutCreditOrderDialogContent = React.forwardRef((props, ref) => {
    const { sourceData = {}, options } = props;
    const { orders, invoiceSetting, member } = sourceData;
    const { coachOptions: employeeOptions } = options;
    const [paidData, setPaidData] = useState({
        total: 0,
        paidPrice: 0,
    });
    const employeeRef = useRef(null);
    const creditOrderRef = useRef(null);
    const checkoutRef = useRef(null);
    const invoiceRef = useRef(null);

    let calculateTotalData = () => {
        if (creditOrderRef && creditOrderRef.current && creditOrderRef.current.getTotalData) {
            setPaidData(creditOrderRef.current.getTotalData());
        }
    };

    useImperativeHandle(ref, () => ({
        getResult: () => {
            let result = {
                employeeID: employeeRef.current.getResult(),
            };
            if (creditOrderRef.current && creditOrderRef.current.getResult) {
                result = Object.assign(result, creditOrderRef.current.getResult());
            } else {
                result.checkoutOrderIDs = [];
            }
            if (checkoutRef.current && checkoutRef.current.getResult) result = Object.assign(result, checkoutRef.current.getResult());
            if (invoiceRef.current && checkoutRef.current.getResult) result = Object.assign(result, { invoiceSetting: invoiceRef.current.getResult() });
            return result;
        },
        isError: () => refIsRequiredError(creditOrderRef, checkoutRef, invoiceRef),
    }));

    return (
        <DialogContent className={'bg-blue-gray-50 container main-container-spacing'}>
            <Grid spacing={2} container>
                <Grid xs={4} item></Grid>
            </Grid>
            <CreditOrderList ref={creditOrderRef} orders={orders} updateOrderTotal={calculateTotalData} />
            <Grid spacing={2} container>
                <Grid xs={6} item>
                    <InvoiceCard ref={invoiceRef} sourceData={{ ...invoiceSetting, issuingEmail: member?.issuingEmail || '' }} options={options} showEmail />
                </Grid>
                <Grid xs={6} item>
                    <CheckoutPaidListBox className="mb-4" ref={checkoutRef} receivableAmount={paidData.total} paidPrice={paidData.paidPrice} />
                    <EmployeeSelect ref={employeeRef} options={employeeOptions} label="擔當" direction="row" fullWidth required />
                </Grid>
            </Grid>
        </DialogContent>
    );
});
const CheckoutCreditOrderDialog = React.forwardRef((props, ref) => {
    const { className, open, title = '欠費補款', sourceData: sourceDataProps = {}, options: optionsProps, onClose, refresh } = props;
    if (!open) return null;
    const { memberID } = sourceDataProps || {};
    // 智能月費: 檢查是否使用智能系統欄位欄位
    const { getMemberInfoCardApi } = useOrderCheckoutApi();
    const { postOrderCheckoutByPeopleApi } = useOrderCheckoutApi();
    const [sourceData, setSourceData] = useState(null);
    const [options, setOptions] = useState(null);
    const contentRef = useRef(null);

    const getParams = () => {
        let result = {
            receiverType: 1,
            receiverID: memberID,
            orderDate: parseDate(),
            items: [],
            products: [],
            purchaseTimeType: 0,
            purchaseToolType: 0,
        };
        if (contentRef.current && contentRef.current.getResult) {
            Object.assign(result, contentRef.current.getResult());
        }
        return result;
    };

    const handleSubmit = () => {
        let isError = contentRef.current && contentRef.current.isError();
        if (!isError) {
            const params = getParams();
            doOrderCheckoutByPeopleApi(params);
        }
    };

    const doOrderCheckoutByPeopleApi = async (params) => {
        let resp = await postOrderCheckoutByPeopleApi(params);
        const { isError = false } = resp || {};
        if (isError) {
            // 0002 錯誤
        } else if (resp) {
            isFunction(refresh) && refresh(memberID);
            onClose();
        }
    };

    const doInitOrderAndOptionsApi = async (memberID) => {
        let res = await getMemberInfoCardApi(memberID);
        if (res) {
            const { orderData = {}, resource } = res;
            isEmpty(options) && setOptions(resource);
            setSourceData(orderData);
        }
    };

    useEffect(
        () => {
            if (open) {
                if (isEmpty(optionsProps)) {
                    doInitOrderAndOptionsApi(memberID);
                } else {
                    setOptions(optionsProps);
                    setSourceData(sourceDataProps);
                }
            }
        },
        // eslint-disable-next-line
        [open],
    );

    return (
        <Dialog
            className={clsx('simple-content', 'dialog', className)}
            PaperProps={{ className: 'wx-90rem', component: Form }}
            onSubmit={handleSubmit}
            open={open}
            fullWidth
        >
            <GradientColorHeader onClose={onClose}>{title}</GradientColorHeader>
            {Array.isArray(sourceData?.orders) && sourceData.orders.length > 0 ? (
                <React.Fragment>
                    <CheckoutCreditOrderDialogContent ref={contentRef} sourceData={sourceData} options={options} />
                    <DialogActions className="bg-blue-gray-50">
                        <Button type="submit" variant="contained">
                            確認送出
                        </Button>
                    </DialogActions>
                </React.Fragment>
            ) : (
                <NoInformationDialogContent />
            )}
        </Dialog>
    );
});

export default CheckoutCreditOrderDialog;
